import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import ObjectId from 'bson-objectid';
import { addDays, format } from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { BehaviorSubject, Observable, catchError, combineLatest, map, of, shareReplay, startWith, tap } from 'rxjs';

import { Team } from '@ninety/platform/models/_shared/team';
import { MeetingType } from '@ninety/platform/models/meetings/meeting-type.enum';
import { teamAll, teamNone } from '@ninety/platform/models/team-list/team-list.model';
import { StateService, extractValueFromStore } from '@ninety/platform/services';
import { TeamsApiService } from '@ninety/platform/services/services/team-api.service';
import {
  NotificationActions,
  TeamSelectors,
  selectActiveUsers,
  selectCurrentUserId,
  selectCurrentUserEffectiveTimezone,
  selectLanguage,
} from '@ninety/platform/state';
import { TerraDateInputModule, TerraFormFieldModule, TerraIconModule, TerraSelectModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';
import {
  CalendarEvent,
  CalendarEventsService,
  ChooseCalendarProviderDialogComponent,
  GoogleCalendarService,
  isOutlookCalendarProvider,
  NativeIntegration,
  NativeIntegrationCoreService,
  OutlookCalendarService,
} from '@ninety/web/pages/shared/integrations';

import { Cadence, CadenceFormOptions, AddMeetingScheduleModel } from '../../../_models';
import { MeetingBotCalendarSectionComponent } from '../../../_shared/meeting-bot-calendar-section/meeting-bot-calendar-section.component';
import {
  toCombinedAgendaOptionsTiered,
  toNonObserverUsersForTeamAndCoaches,
} from '../../../_state/meetings-agenda-options.logic';
import { MeetingSchedulingActions } from '../../../_state/meetings.actions';
import { MeetingsStateSelectors } from '../../../_state/meetings.selectors';
import { SelectCalendarEventDialogComponent } from '../../start-meeting-dialog/select-calendar-event-dialog/select-calendar-event-dialog.component';

import {
  TimeOption,
  toNextTopOfHour,
  toTimeOptions,
  toZonedTimeString,
  withExtraTimeOption,
} from './schedule-meeting-time.logic';

export interface ScheduleMeetingDialogData {
  teamId: string | null;
}

type TeamState = { status: 'loading' } | { status: 'ready'; team: Team | null } | { status: 'error' };

@Component({
  selector: 'ninety-schedule-meeting-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TerraIconModule,
    LetDirective,
    ButtonComponent,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TerraFormFieldModule,
    TerraSelectModule,
    TerraDateInputModule,
    SelectCalendarEventDialogComponent,
    MeetingBotCalendarSectionComponent,
    ChooseCalendarProviderDialogComponent,
  ],
  templateUrl: './schedule-meeting-dialog.component.html',
  styleUrls: ['./schedule-meeting-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleMeetingDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);

  private readonly store = inject(Store);
  private readonly data = inject<ScheduleMeetingDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly teamApiService = inject(TeamsApiService);
  private readonly dialogRef = inject(MatDialogRef<ScheduleMeetingDialogComponent>);
  private readonly stateService = inject(StateService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly dateAdapter = inject(DateAdapter<any>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly nativeIntegrationService = inject(NativeIntegrationCoreService);
  private readonly googleCalendarService = inject(GoogleCalendarService);
  private readonly outlookCalendarService = inject(OutlookCalendarService);

  form: FormGroup;
  private readonly _timeOptions = signal<readonly TimeOption[]>(toTimeOptions());
  readonly timeOptions = this._timeOptions.asReadonly();
  protected cadence$ = new BehaviorSubject<{ name: string; value: CadenceFormOptions }[]>([]);
  minDate = new Date();
  readonly pickedEvent = signal<CalendarEvent | undefined>(undefined);
  readonly addMeetingAgentControl = new FormControl(false);

  // Only needed here to resolve the connectionId for the swapped event picker below - the rest of
  // the calendar-connect UI/state lives in MeetingBotCalendarSectionComponent.
  readonly activeCalendarConnectionId = computed(() => this.nativeIntegrationService.activeCalendarConnectionId());

  readonly calendarEventsService = computed<CalendarEventsService>(() => {
    const integration = this.nativeIntegrationService.activeCalendarIntegration();
    return integration && isOutlookCalendarProvider(integration)
      ? this.outlookCalendarService
      : this.googleCalendarService;
  });

  // Swaps this dialog's own content for the event picker in place, rather than opening it as a
  // nested modal on top.
  readonly showCalendarPicker = signal(false);

  // Same swap-in-place pattern for the choose-provider step, so the meeting dialog stays in the
  // background instead of a second dialog stacking on top of it.
  readonly showProviderChooser = signal(false);
  readonly providerChooserIntegrations = signal<NativeIntegration[]>([]);

  private readonly teamState$: Observable<TeamState> = this.data?.teamId
    ? this.teamApiService.getTeamById(this.data.teamId).pipe(
        map((team): TeamState => ({ status: 'ready', team })),
        catchError(() => of<TeamState>({ status: 'error' })),
        startWith<TeamState>({ status: 'loading' }),
        shareReplay({ bufferSize: 1, refCount: false })
      )
    : this.store.select(TeamSelectors.selectFilterBarTeam).pipe(map((team): TeamState => ({ status: 'ready', team })));

  private readonly teamState = toSignal(this.teamState$, { initialValue: { status: 'loading' } as TeamState });

  private readonly team = computed(() => {
    const state = this.teamState();

    return state.status === 'ready' ? state.team : null;
  });

  readonly teamLoading = computed(() => this.teamState().status === 'loading');

  readonly teamUnavailable = computed(() => {
    if (this.teamLoading() || this.teamState().status === 'error') return true;

    const teamId = this.team()?._id;

    return !teamId || teamId === teamAll._id || teamId === teamNone._id;
  });

  vm$ = {
    language: this.store.select(selectLanguage),
    agendas: combineLatest([
      this.teamState$,
      this.store.select(MeetingsStateSelectors.selectAgendaDerivationContext),
    ]).pipe(
      map(([state, context]) =>
        state.status === 'ready' ? toCombinedAgendaOptionsTiered({ ...context, team: state.team }) : []
      ),
      tap(agendas => {
        //Note: we default to the first agenda if there is only one
        //happens when on essential tier (for now), only weekly meetings are available
        const control = this.form.get('agenda');

        if (agendas.length === 1) {
          control.setValue(agendas[0]);
          control.disable();
          return;
        }

        if (control.disabled) control.enable();

        if (control.value) return;

        const levelTen = agendas.find(agenda => agenda.id === MeetingType.weekly);
        if (levelTen) control.setValue(levelTen);
      })
    ),
    users: combineLatest([this.store.select(selectActiveUsers), this.teamState$]).pipe(
      map(([users, state]) =>
        state.status === 'ready' ? toNonObserverUsersForTeamAndCoaches(users, state.team?._id ?? null) : []
      )
    ),
  };

  constructor() {
    this.nativeIntegrationService.fetchNativeIntegrations();

    effect(() => {
      if (this.teamState().status !== 'error') return;

      this.store.dispatch(
        NotificationActions.notifyError({
          error: new Error(`Could not load team ${this.data?.teamId}`),
          message: 'Could not load that team. Please try again.',
        })
      );
      this.dialogRef.close();
    });
  }

  ngOnInit(): void {
    const defaultStart = toNextTopOfHour(new Date(), this.timezone());
    this.cadence$.next(this.getCadenceOptions(defaultStart.date));

    this.form = this.fb.group({
      agenda: ['', Validators.required],
      date: [defaultStart.date, Validators.required],
      time: [defaultStart.time, Validators.required],
      cadence: [CadenceFormOptions.none, Validators.required],
      presenterId: [extractValueFromStore(this.store, selectCurrentUserId), Validators.required],
      scribeId: [],
    });

    // Temp until we can redo localeChange from a single place in the app
    this.stateService.localeChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: l => {
        this.dateAdapter.setLocale(l);
      },
    });
  }

  dateChanged(date: Date) {
    this.cadence$.next(this.getCadenceOptions(date));
    this.form.patchValue({ cadence: CadenceFormOptions.none });
  }

  openEventPicker(): void {
    if (!this.activeCalendarConnectionId()) {
      return;
    }
    this.showCalendarPicker.set(true);
  }

  onCalendarClosed(event: CalendarEvent | undefined): void {
    this.showCalendarPicker.set(false);
    if (!event) {
      return;
    }

    this.pickedEvent.set(event);
    if (event.start) {
      this.applyEventDateTime(new Date(event.start));
    }
  }

  onOpenProviderChooser(integrations: NativeIntegration[]): void {
    this.providerChooserIntegrations.set(integrations);
    this.showProviderChooser.set(true);
  }

  onProviderChooserClosed(): void {
    this.showProviderChooser.set(false);
  }

  // Prefills the date/time/cadence fields from the picked event so the user doesn't have to
  // re-enter what the calendar already knows - defaults cadence to weekly, the common case for a
  // recurring meeting series.
  private applyEventDateTime(date: Date): void {
    const time = toZonedTimeString(date, this.timezone());

    this._timeOptions.set(withExtraTimeOption(toTimeOptions(), time));
    this.cadence$.next(this.getCadenceOptions(date));
    this.form.patchValue({ date, time, cadence: CadenceFormOptions.weekly });
  }

  private timezone(): string {
    return extractValueFromStore(this.store, selectCurrentUserEffectiveTimezone);
  }

  save() {
    const team = this.team();
    if (this.teamUnavailable()) return;

    this.store.dispatch(MeetingSchedulingActions.addMeetingSchedule({ ...this.formToAddMeetingModel(), team }));
    this.dialogRef.close();
  }

  formToAddMeetingModel(): {
    schedule: Omit<AddMeetingScheduleModel, 'teamId'>;
  } {
    const { agenda, date, cadence, time, presenterId, scribeId } = this.form.getRawValue();

    const meetingType = ObjectId.isValid(agenda.id) ? MeetingType.custom : agenda.id;
    const agendaId = ObjectId.isValid(agenda.id) ? agenda.id : null;

    const timezone = this.timezone();

    // Build date-time string in user's timezone, then convert to UTC. `time` is already 24-hour
    // `HH:mm`, straight off the select.
    const formattedTimezoneDate = formatInTimeZone(date, timezone, 'yyyy-MM-dd');
    const dateTime = zonedTimeToUtc(`${formattedTimezoneDate}T${time}:00`, timezone);

    return {
      schedule: {
        meetingType,
        agendaId,
        presenterId,
        scribeId: scribeId || null,
        cadence: cadence === CadenceFormOptions.monthlyLastWeek ? Cadence.monthly : cadence,
        lastWeekOfMonth: cadence === CadenceFormOptions.monthlyLastWeek,
        cadenceStartDate: dateTime,
        meetingLink: this.addMeetingAgentControl.value ? (this.pickedEvent()?.meetingUrl ?? null) : null,
        parentId: null,
      },
    };
  }

  getCadenceOptions(date: Date): { name: string; value: CadenceFormOptions }[] {
    const zoned = utcToZonedTime(date, this.timezone());

    const cadenceOptions = [
      { name: 'does not repeat', value: CadenceFormOptions.none },
      { name: `Weekly on ${format(zoned, 'EEEE')}`, value: CadenceFormOptions.weekly },
      { name: `Every 2 weeks on ${format(zoned, 'EEEE')}`, value: CadenceFormOptions.biWeekly },
    ];

    const plus7 = addDays(zoned, 7);
    const isLastNthDayOfMonth = plus7.getMonth() !== zoned.getMonth();
    const dayNum = zoned.getDate();
    const nthWeekOfTheMonth = Math.floor((dayNum - 1) / 7) + 1;

    if (nthWeekOfTheMonth === 4 && isLastNthDayOfMonth) {
      return [
        ...cadenceOptions,
        { name: `Monthly on the fourth ${format(zoned, 'EEEE')}`, value: CadenceFormOptions.monthly },
        { name: `Monthly on the last ${format(zoned, 'EEEE')}`, value: CadenceFormOptions.monthlyLastWeek },
      ];
    }

    if (nthWeekOfTheMonth === 5) {
      return [
        ...cadenceOptions,
        { name: `Monthly on the last ${format(zoned, 'EEEE')}`, value: CadenceFormOptions.monthlyLastWeek },
      ];
    }

    const ordinals = ['first', 'second', 'third', 'fourth', 'fifth'];
    return [
      ...cadenceOptions,
      {
        name: `Monthly on the ${ordinals[Math.floor((dayNum - 1) / 7)]} ${format(zoned, 'EEEE')}`,
        value: CadenceFormOptions.monthly,
      },
    ];
  }
}
